// Central product catalogue for ITL Technical Services
export const PRODUCTS = [
  {
    slug: "servo-voltage-stabilizers",
    index: "01",
    name: "Servo Controlled AC Voltage Stabilizers",
    short: "Servo Stabilizers",
    tagline: "Air / oil cooled · single & three phase · BIS (ISI) 9815:1994",
    image: "./product1.jpg",
    banner: "/ServoStabilizerBanner.jpg",

    description:
      "Air/oil cooled, servo controlled automatic AC voltage stabilizers for single and three-phase operation, built to BIS (ISI) 9815:1994. Delivers rated power and regulated output voltage without waveform distortion, overshoot or undershoot across the specified input range. Output adjustable within ±5% of rated voltage via front-panel potentiometer with lock nut.",
    specs: [
      ["Input Frequency", "50Hz ± 6%"],
      ["Output Regulation", "±1% (internally settable 0.5%–2.0%)"],
      ["Efficiency", "Up to 90%"],
      ["Ambient Temperature", "45°C max"],
      ["Humidity", "Up to 90%"],
      ["Indications", "Input Low / Input High / Output ON"],
      ["Metering", "AC Voltmeter with input/output selector switch"],
      ["Controls", "Auto/Manual switch with spring-loaded increase/decrease"],
      ["Standard Input Voltage", "170–270V (1Φ) / 295–465V (3Φ, 4-wire RYBN)"],
      ["Standard Output Voltage", "230V (1Φ) / 400V (3Φ) or as required"],
    ],
    features: [
      "Special electronic circuit for automatic operation on generator and mains supply",
      "Wide input voltage range",
      "Plug-in type PCB and modular construction",
      "Control circuit isolated from mains",
      "Ideal for high starting-current loads like motors",
      "Rugged AC synchronous motor for long life",
      "High efficiency, reliable IC-based control",
      "High-speed correction and quick response",
      "Unaffected by load power factor / phase shift",
      "No oscillation, no overshoot, no undershoot",
      "No waveform distortion, no noise generation",
      "Compact size, mounted on wheels for easy movement",
      "Designed to minimize losses",
      "High-quality lamination and enameled electrolytic copper windings",
      "Oil level indicator, oil inlet/outlet for oil-cooled units",
      "Lifting hooks and grounding bolts where required",
    ],
    optional: [
      "Electronic overload and short-circuit protection",
      "Over-voltage and under-voltage trip protection",
      "Single-phase preventer for three-phase stabilizers",
      "Audio indication for over/under voltage",
      "Auto switch-off on phase reversal (3-phase units)",
      "Built-in stabilizer bypass arrangement / changeover switch",
      "Built-in step-up/step-down isolation transformer",
      "Built-in RF/spike & surge suppressor",
      "Fuse / HRC fuse / fuse switch unit for input/output",
      "3-digit LED AC voltmeter (in place of analog)",
      "AC ammeter (digital/analog) on load side",
      "Digital frequency meter with low/high frequency cutoff",
      "Transformer oil as per IS:335 for oil-cooled units",
    ],
    faqs: [
      {
        q: "What is the difference between a servo stabilizer and a CVT?",
        a: "A servo stabilizer uses a servo motor and buck/boost transformer to continuously correct voltage across a wide input range and is ideal for high-power industrial loads. A CVT (Constant Voltage Transformer) is a ferro-resonant device with no moving parts, offering instantaneous correction for smaller, spike-sensitive electronic loads.",
      },
      {
        q: "Can a servo stabilizer handle high starting-current loads like motors?",
        a: "Yes. Our servo stabilizers are specifically designed with a rugged AC synchronous motor and IC-based control to handle high starting-current loads such as motors, compressors and CNC machines without overshoot.",
      },
      {
        q: "What input voltage range does a three phase servo stabilizer accept?",
        a: "The standard three-phase input range is 295–465V (4-wire RYBN) with a regulated 400V output, and wider ranges can be custom built to your site conditions.",
      },
      {
        q: "Are your stabilizers BIS/ISI certified?",
        a: "Yes, our servo controlled voltage stabilizers are manufactured in conformance with BIS (ISI) 9815:1994.",
      },
    ],
  },
  {
    slug: "isolation-transformers",
    index: "02",
    name: "Super Isolation / Noise Cut-Off Transformers",
    short: "Isolation Transformers",
    tagline: "Ultra-isolation · noise cut-off · BIS (ISI) IS:2026",
    image: "./product2.jpg",
    banner: "/IsolationTransformerBanner.jpg",
    description:
      "Highly efficient, reliable power sources for electronic, electrical, electromechanical, medical and office automation equipment. Supplied as control, step-up, step-down, Delta/Delta, Star/Star and Delta/Star transformers — air or oil cooled, single or three phase — conforming to BIS (ISI) IS:2026. Provides high electrostatic insulation resistance and isolates equipment from high-frequency noisy power lines.",
    specs: [
      ["Input Voltage", "100/110/230/240/400/415V (1Φ or 3Φ)"],
      ["Output Voltage", "1:1 / step-up / step-down or as required"],
      ["Acceptable Input Variation", "±10% of rated voltage"],
      ["Line Frequency", "47–53 Hz"],
      ["Load Regulation", "Better than 3.5%"],
      ["Insulation Resistance", "1000 MΩ at 500V DC"],
      ["Breakdown Voltage", "2500V AC for 1 minute (H.V. test)"],
      ["Leakage Current", "Less than 1 microampere"],
      ["Cooling", "Naturally air cooled / oil cooled"],
      ["Efficiency", "Better than 95%"],
      ["Ambient Temperature", "45°C max"],
      ["Humidity", "Up to 90%"],
      ["Indications", "Output ON"],
      ["Enclosure", "Open type / suitable metal enclosure"],
      ["Termination", "Flying leads / terminal blocks or as required"],
    ],
    features: [
      "Designed to minimize losses",
      "High-quality lamination and electrolytic enameled copper meeting international standards",
      "Oil level indicators, oil inlet/outlet, lifting hooks, grounding bolts (oil-cooled units)",
    ],
    optional: [
      "AC voltmeter for input/output measurement",
      "AC ammeter on load side",
      "Fuse / HRC fuse / fuse switch unit for input/output",
      "Transformer oil as per IS:335 for oil-cooled transformers",
      "Thermometer pocket for oil-cooled transformers",
    ],
    faqs: [
      {
        q: "What does a super isolation transformer protect against?",
        a: "It provides high electrostatic insulation and isolates sensitive equipment from high-frequency line noise, common-mode noise, spikes and ground loops — critical for medical, IT and precision electronic equipment.",
      },
      {
        q: "What is the insulation resistance of your isolation transformers?",
        a: "Our units offer an insulation resistance of 1000 MΩ at 500V DC and withstand a breakdown voltage of 2500V AC for 1 minute during high-voltage testing.",
      },
      {
        q: "Are isolation transformers available in three phase?",
        a: "Yes — we supply single and three phase units in Delta/Delta, Star/Star and Delta/Star configurations, air or oil cooled, conforming to IS:2026.",
      },
    ],
  },
  {
    slug: "constant-voltage-transformers",
    index: "03",
    name: "Constant Voltage Transformers (CVTs)",
    short: "CVTs",
    tagline: "Ferro-resonant · no moving parts · instant correction",
    image: "./product3.jpg",
    banner: "/CVTBAnner.jpg",
    description:
      "Provide instantaneous voltage regulation for applications where output fluctuation and spikes can affect sensitive equipment. A ferro-resonant design with no moving parts ensures trouble-free service and a fast correction rate.",
    specs: [
      ["Input Voltage", "180–260V / 300–460V"],
      ["Output Voltage", "220V or 230V / 380V or 400V"],
      ["Frequency", "50 Hz"],
      ["Regulation", "±1% from 50% to full load"],
      ["Metering", "AC Voltmeter"],
      ["Transformer Type", "Ferro Resonant"],
      ["Operating / Ambient Temp", "20°C to 50°C"],
      ["Efficiency", "About 90%"],
      ["Output Response Time", "1½ cycles (30 milliseconds)"],
      ["Load Power Factor", "0.8 lag to 0.8 lead"],
    ],
    features: [
      "Wide input voltage range for loads lower than rated load",
      "No output loss during line interruptions up to 3 milliseconds",
      "Electromagnetically shielded",
      "Output is floating and highly isolated from the line",
      "Output regulation time of 30 milliseconds",
      "No moving parts — trouble-free service and fast correction rate",
    ],
    optional: [],
    faqs: [
      {
        q: "How fast does a CVT correct voltage?",
        a: "A CVT corrects output voltage within about 1½ cycles (30 milliseconds) thanks to its ferro-resonant design, making it ideal for spike-sensitive electronic loads.",
      },
      {
        q: "Does a CVT have moving parts?",
        a: "No. A Constant Voltage Transformer is a purely ferro-resonant device with no moving parts, which means trouble-free service and a fast correction rate.",
      },
      {
        q: "When should I choose a CVT over a servo stabilizer?",
        a: "Choose a CVT for smaller, sensitive electronic loads that require instantaneous correction and isolation from spikes. For high-power industrial loads with a wide input range, a servo stabilizer is more suitable.",
      },
    ],
  },
  {
    slug: "electrical-equipment-supply",
    index: "04",
    name: "Electrical & Electronic Equipment Supply",
    short: "Electrical Supply",
    tagline: "Batteries · inverters · solar power systems",
    image: "./product4.jpeg",
    banner: "/AllGoods.jpg",
    description:
      "Beyond in-house manufacturing, ITL Technical Services supplies a broad range of electrical and electronic equipment including industrial batteries, inverters and solar power systems. We work as a trusted supply and trading partner, sourcing quality-assured products to complement your power infrastructure. Share your requirement and our team will provide detailed catalogs and a tailored quotation.",
    specs: [],
    features: [
      "Industrial and tubular batteries",
      "Home & industrial inverters",
      "On-grid / off-grid solar power systems",
      "Complementary electrical & electronic components",
      "Quality-assured sourcing and supply",
      "Bulk and project-based procurement support",
    ],
    optional: [],
    faqs: [
      {
        q: "Do you supply solar power systems and inverters?",
        a: "Yes. We supply on-grid and off-grid solar power systems, inverters and batteries as part of our general electrical and electronic equipment supply capability. Contact us with your load requirement for a detailed quote.",
      },
      {
        q: "Can you handle bulk or project-based orders?",
        a: "Absolutely. We support bulk and project-based procurement. Reach out with your bill of materials and delivery timeline for pricing.",
      },
    ],
  },
];

export const getProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);

export const CERTIFICATIONS = [
  "BIS / ISI CERTIFIED",
  "ISO 9001:2015",
  "MADE IN INDIA",
  "CUSTOM BUILT",
];

export const COMPANY = {
  name: "ITL Technical Service",
  phone: "+91 85000 87756",
  email: "jayaramreddy95@gmail.com",
  address:
    "A-314 , PHASE-2 , ALLWYNCOLONY , KUKATPALLY , HYDERABAD 500072 India",
  hours: "Mon – Sat · 9:00 AM – 6:30 PM",
};
export const MANUFACTURING_PROCESS = [
  {
    step: "01",
    title: "Technical Consultation & Design",
    description:
      "Our applications engineers analyze your specific voltage profiles, load profiles, and machinery requirements to design custom winding geometries and magnetic cores.",
  },
  {
    step: "02",
    title: "Precision Engineering",
    description:
      "Windings are calculated using 99.9% pure electrolytic grade copper. Enclosures are drafted in 3D CAD with detailed airflow simulations for optimal thermal cooling.",
  },
  {
    step: "03",
    title: "Core & Coil Assembly",
    description:
      "Coils are wound tightly with double-paper or double-glass insulation and assembled onto high-permeability, low-loss CRGO silicon steel cores for maximum energy efficiency.",
  },
  {
    step: "04",
    title: "Vacuum Pressure Impregnation (VPI)",
    description:
      "Assemblies undergo complete vacuum baking and varnish pressure impregnation. This process eliminates microscopic air pockets, increases dielectric strength, and prevents vibration.",
  },
  {
    step: "05",
    title: "Enclosure Fabricating & Fitting",
    description:
      "Heavy-gauge steel enclosures are powder-coated with anti-corrosive finishes, fitted with high-efficiency radiators or forced-cooling ventilation blocks, and wired with control electronics.",
  },
  {
    step: "06",
    title: "Rigorous Factory Testing",
    description:
      "Every unit undergoes strict quality inspection, including insulation resistance, high-voltage breakdown, no-load loss, load loss, temperature rise, and full load simulation.",
  },
  {
    step: "07",
    title: "Secure Packaging & Shipping",
    description:
      "Finished equipment is secured in heavy-duty wooden sea-worthy crates with moisture barriers and shock absorbers to guarantee safe, scratch-free transport to your facility.",
  },
  {
    step: "08",
    title: "Site Installation & AMC Support",
    description:
      "Our field engineers complete on-site installation, grounding audits, commissioning, and live load testing. We support your equipment over its entire lifespan via AMCs.",
  },
];
export const INDUSTRIES = [
  {
    name: "Manufacturing & Heavy Industries",
    description: "CNC Machining, Steel, Cement, Textile and Automotive Plants",
  },
  {
    name: "Healthcare & Super-specialty Hospitals",
    description:
      "Delicate MRI scanner suites, CT Scanners, and critical ICU life support systems",
  },
  {
    name: "Data Centers & Cloud Server Farms",
    description:
      "Galvanic noise isolation, harmonic mitigation, and ultra-reliable continuous power distribution",
  },
  {
    name: "Infrastructure & Commercial Buildings",
    description:
      "High-rise elevators, commercial HVAC installations, and intelligent building controls",
  },
  {
    name: "Renewable & Solar Power Systems",
    description:
      "Inverter step-up isolation systems, solar grid voltage stabilization, and wind energy plants",
  },
  {
    name: "Oil, Gas, and Chemical Refineries",
    description:
      "Hazardous area compliant (explosion-proof options) oil-immersed voltage systems",
  },
];
export const SERVICES = [
  {
    id: "installation-commissioning",
    title: "Installation & Commissioning",
    description:
      "Expert installation of stabilizers, industrial transformers, and power backup equipment by our field engineers.",
    icon: "ShieldAlert",
    details: [
      "Site electrical assessment and structural verification prior to equipment delivery.",
      "Precise physical positioning, levelling, and heavy cable termination.",
      "In-situ insulation testing, grounding checks, and phase calibration.",
      "Live power commissioning, load testing, and direct technical hand-off to site operators.",
    ],
  },
  {
    id: "preventive-maintenance",
    title: "Preventive Maintenance & AMC",
    description:
      "Structured Annual Maintenance Contracts (AMC) designed to eliminate downtime and keep equipment running at peak efficiency.",
    icon: "CheckCircle",
    details: [
      "Regular scheduled site visits by certified industrial engineers.",
      "Thermal imaging of electrical terminals to identify and fix hot spots before failure.",
      "Cleaning of dust accumulation, mechanical lubrication of servo gears, and carbon brush wear checks.",
      "Transformer oil acidity testing, dielectric strength checking, and filtration.",
    ],
  },
  {
    id: "testing-calibration",
    title: "Testing & Diagnostic Calibration",
    description:
      "Comprehensive electrical validation and diagnostic testing to maintain compliance with industrial engineering standards.",
    icon: "Activity",
    details: [
      "High-voltage dielectric breakdown (hipot) testing.",
      "Harmonic analysis and power quality logging over continuous 24-hour cycles.",
      "Calibration of analog/digital metering circuits, protection relay limits, and speed sensors.",
      "Standard certified test reports verifying compliance with Indian standards (IS 2026/IS 9815).",
    ],
  },
  {
    id: "repair-reconditioning",
    title: "Repair & Reconditioning",
    description:
      "Emergency on-site repair and workshop reconditioning to restore damaged transformers and stabilizers to factory specifications.",
    icon: "Settings",
    details: [
      "24/7 priority emergency response for critical process breakdowns.",
      "Full winding re-winding using high-grade electrolytic copper and Class-H insulation.",
      "Replacement of worn servo motor gears, carbon brushes, and logic motherboards.",
      "Complete vacuum impregnation and baking to restore insulation properties.",
    ],
  },
];
export const FAQS = [
  {
    category: "General",
    question:
      "Who is ITL Technical Service and what are their core manufacturing capabilities?",
    answer:
      "ITL Technical Service is an established Indian manufacturer specializing in industrial electrical engineering and power quality solutions. We design, build, and service high-grade Servo Voltage Stabilizers, Isolation Transformers, Constant Voltage Transformers, and custom step-up/step-down transformers. Our production facilities use pure electrolytic copper, low-loss CRGO cores, and advanced testing setups to ensure international standards of engineering excellence.",
  },
  {
    category: "Servo Stabilizers",
    question:
      "What is the difference between an Air-Cooled and an Oil-Cooled Servo Stabilizer?",
    answer:
      "Air-Cooled Servo Stabilizers use forced-air fans for heat dissipation and are typically designed for capacities up to 100 kVA. They are clean, lightweight, and ideal for indoor environments like laboratories, IT centers, and offices. Oil-Cooled Servo Stabilizers are immersed in transformer mineral oil, providing superior cooling and protection against dust, humidity, and corrosive fumes. They are built for heavy-duty applications above 100 kVA up to 2000 kVA, and are highly recommended for hot, dusty, or outdoor industrial locations like steel mills, chemical plants, and heavy engineering factories.",
  },
  {
    category: "Servo Stabilizers",
    question:
      "Why should we choose a Servo Stabilizer over a static or electronic stabilizer for heavy machinery?",
    answer:
      "For heavy industrial machinery with motors, CNC spindles, or high inrush startup currents, Servo Stabilizers are ideal because they use a rugged motor-driven variable autotransformer. They offer high surge overload capacities (handling up to 300% overload for short durations), zero waveform distortion, and highly linear, precise output voltage regulation (±1%). Static electronic stabilizers, while faster, can have limited overload tolerances and may experience semiconductor stress during heavy motor startups.",
  },
  {
    category: "Isolation Transformers",
    question: "What is the primary function of an Isolation Transformer?",
    answer:
      "An Isolation Transformer provides galvanic isolation by physically separating the primary utility supply from the secondary load circuit through magnetic coupling. This eliminates ground loop currents, blocks high-frequency common-mode electrical noise (attenuation > 120 dB), and prevents electric shock. It also creates a fresh, noise-free local ground-to-neutral reference on the secondary side, which is vital for microprocessor logic and medical monitors.",
  },
  {
    category: "Isolation Transformers",
    question: "What is a Super Isolation Transformer and when is it required?",
    answer:
      "A Super Isolation Transformer is an ultra-high performance isolation transformer equipped with triple electrostatic copper shields. This design reduces the coupling capacitance between the windings to less than 0.005 pF, achieving exceptional common-mode noise attenuation exceeding 140 dB. It is required for highly sensitive micro-electronic laboratory equipment, precision nanotechnology tools, scientific analyzers, and military telemetry systems where even minor electrical noise can corrupt sensitive data.",
  },
  {
    category: "CVT",
    question:
      "How does a Constant Voltage Transformer (CVT) work and what are its advantages?",
    answer:
      "A Constant Voltage Transformer (CVT) operates on the principle of ferroresonance (a saturated magnetic core resonant circuit). Since it is entirely solid-state with no moving parts, gears, brushes, or relays, it has virtually infinite reliability and zero mechanical wear. It provides instantaneous voltage regulation (reacts in 10-15 milliseconds), shields against line surges, and features built-in current limiting protection. It also has a 'ride-through' capability to bridge microsecond-long power dropouts.",
  },
  {
    category: "CVT",
    question: "Is a CVT suitable for large inductive motor loads?",
    answer:
      "No, CVTs are generally not recommended for large motor loads or highly inductive machinery. This is because their ferroresonant tank circuit is sensitive to heavy current surges, which can cause the output voltage to sag and go into a current-limiting shutdown state. CVTs are optimized for small, sensitive electronic equipment under 10 kVA, such as medical analyzers, computerized weighing scales, POS terminals, and PLC cards.",
  },
  {
    category: "Transformers",
    question:
      "Do you manufacture custom Step-Up or Step-Down Transformers for imported machinery?",
    answer:
      "Yes, this is one of our core specialties. Many machines imported from the USA, Japan, or Europe run on voltages like 110V, 200V, 220V, 380V, or 480V, whereas the standard Indian grid is 415V (3-phase) or 230V (1-phase). We engineer custom-designed step-up or step-down transformers (both dry and oil-cooled) with precise input and output voltage taps to match your imported machinery perfectly.",
  },
  {
    category: "Services",
    question:
      "What does your Annual Maintenance Contract (AMC) for stabilizers and transformers cover?",
    answer:
      "Our comprehensive AMC covers quarterly scheduled visits by certified field engineers. During these visits, we perform thermal scanning of terminals to identify hot spots, check carbon brushes and servo drive gears, verify protection circuit cut-off thresholds, clean dust accumulation, check grounding impedance, and test transformer oil dielectric strength. It also includes priority emergency breakdown support with guaranteed on-site response times to minimize unplanned downtime.",
  },
  {
    category: "General",
    question:
      "Are your electrical products compliant with Indian Standards and BIS regulations?",
    answer:
      "Yes, all ITL Technical Service products are designed, manufactured, and rigorously tested in compliance with relevant Indian and international engineering standards, such as IS 2026 (for power transformers) and IS 9815 (for servo stabilizers). We follow strict Quality Assurance procedures, and our products can be supplied with certified test reports from authorized laboratories if requested.",
  },
  {
    category: "General",
    question:
      "What winding materials do you use in your transformers and stabilizers?",
    answer:
      "We use exclusively 99.9% pure electrolytic-grade copper conductors insulated with double-paper or double-glass covering, depending on the temperature rating of the transformer. We do not use aluminum windings in our industrial equipment, as copper provides superior electrical conductivity, mechanical strength, lower thermal expansion, and significantly lower energy losses under continuous operations.",
  },
  {
    category: "General",
    question:
      "What protective safety features are built into your Servo Stabilizers?",
    answer:
      "Our stabilizers are equipped with comprehensive electronic safety circuits. These include high-voltage and low-voltage limits (output cut-off), short-circuit protection, overload trip, phase failure/single-phasing protection, phase reversal warning, and high-temperature alarms. Additionally, we integrate manual or automatic bypass switches so that you can switch to raw utility power during scheduled maintenance without shutting down your facility.",
  },
  {
    category: "Services",
    question:
      "Do you provide on-site electrical consultation and diagnostic testing?",
    answer:
      "Yes. Our team of senior electrical engineering experts provides professional on-site consulting services. This includes logging and analyzing power quality over a continuous 24-hour cycle to check for harmonics, surges, or voltage sags. Based on the logged waveform telemetry, we recommend the exact rating and type of power conditioning equipment (stabilizer, isolation transformer, or CVT) required to stabilize your systems.",
  },
  {
    category: "General",
    question:
      "Can your Servo Stabilizers run continuously with diesel generators?",
    answer:
      "Yes, our stabilizers are engineered to be fully compatible with diesel generators. The control feedback logic is specially tuned with high-frequency noise rejection and delayed-start features. This prevents 'hunting'—a common issue where standard stabilizers continuously shift back and forth due to generator frequency fluctuations, which can stress both the stabilizer motor and the generator governor.",
  },
  {
    category: "General",
    question:
      "What is the typical lifespan and maintenance cycle of an ITL Isolation Transformer?",
    answer:
      "An ITL double-wound isolation transformer manufactured with Class-H copper winding and vacuum-pressure impregnation typically has an operational lifespan exceeding 20 to 25 years under normal operating temperatures. Maintenance is minimal: dry-type units simply require periodic cleaning to remove dust, while oil-immersed units require annual transformer oil dielectric strength testing and acidity checks.",
  },
  {
    category: "General",
    question:
      "How do you handle heavy starting inrush currents of large electric motors?",
    answer:
      "Our industrial and control transformers are designed with excellent regulation and high physical thermal masses, allowing them to support short-duration inrush currents up to 8 times their nominal rating without causing excessive secondary voltage drops. Our servo stabilizers are also equipped with heavy-duty copper-carbon brushes and rugged servo motors to quickly handle starting surges.",
  },
  {
    category: "General",
    question:
      "Where are your products manufactured and do you ship across India?",
    answer:
      "All our products are manufactured in India at our specialized production plants. We supply our power quality and distribution equipment to industrial hubs, hospitals, and infrastructure projects across the entire country, backed by a network of field engineers for fast on-site installation and support.",
  },
  {
    category: "General",
    question:
      "How can I request a customized quotation for our factory equipment?",
    answer:
      "You can request a free customized technical proposal and quote by filling out our B2B Lead Form on our website, or by clicking the WhatsApp/Call buttons to connect directly with an applications engineer. Please provide your input/output voltage requirements, total load in kVA, and the type of machinery you need to power (e.g. CNC, medical scanners, or full-plant load).",
  },
  {
    category: "General",
    question:
      "Do you provide installation services, or does our local electrical contractor handle it?",
    answer:
      "We offer complete end-to-end support. While we can supply the equipment for your local team to install, we highly recommend utilizing our certified field engineers. We handle site preparation audits, mechanical leveling, cable termination, dielectric testing, grounding verification, and full operational commissioning to ensure your product warranty is fully validated.",
  },
  {
    category: "General",
    question: "What is the warranty period for ITL Technical Service products?",
    answer:
      "We stand behind our manufacturing quality. Our standard products come with a comprehensive 12-month on-site warranty covering all parts and labor. We also offer extended warranties up to 36 or 60 months when coupled with our Annual Maintenance Contracts (AMC).",
  },
];
